import { Button } from "@result/rui/components";
import { classNames } from "@result/rui/utils/component";
import PropTypes from "prop-types";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import NavButton from "./NavButton";

/**
 * Page header component, used on most pages to display the title, subtitle and optionally a back button and other actions.
 *
 * Usage example:
 *
 * <PageHeader
 *  title="Page Title"
 *  subtitle="Page subtitle"
 *  backURL="/previous-page"
 * />
 *
 * @param {object} param0
 * @returns
 */
export default function PageHeader({
  title,
  subtitle,
  onBackClick,
  showBackButton: $showBackButton = false,
  backButtonLabel,
  backURL,
  actions,
  stepInfo,
  className,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const showBackButton =
    $showBackButton || !!onBackClick || !!backButtonLabel || !!backURL;

  const handleBackClick = () => {
    if (onBackClick) {
      onBackClick();
    } else {
      navigate(-1);
    }
  };

  const BackButtonEl = backURL ? (
    <NavButton
      to={backURL}
      label={backButtonLabel || t("form.back")}
      leftIcon="icon-chevron-left"
      color="white"
    />
  ) : (
    <Button
      label={backButtonLabel || t("form.back")}
      leftIcon="icon-chevron-left"
      color="white"
      onClick={handleBackClick}
    />
  );

  return (
    <div
      className={classNames(
        "columns is-desktop is-align-items-center mb-4",
        className
      )}>
      <div className="is-flex is-align-items-center">
        {showBackButton && (
          <div className="column is-narrow">{BackButtonEl}</div>
        )}
        <div className="column is-narrow">
          <div className="is-size-4">{title}</div>
          {subtitle && <div>{subtitle}</div>}
        </div>
      </div>
      {(actions || stepInfo) && (
        <div className="column is-flex is-flex-wrap-wrap is-gap-2 is-align-items-center is-justify-content-flex-end">
          {actions}
          {stepInfo && <span className="tag is-black">{stepInfo}</span>}
        </div>
      )}
    </div>
  );
}

PageHeader.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  onBackClick: PropTypes.func,
  showBackButton: PropTypes.bool,
  backButtonLabel: PropTypes.string,
  backURL: PropTypes.string,
  actions: PropTypes.node,
  stepInfo: PropTypes.string,
  className: PropTypes.string,
};
